import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";

import OrderView from './views/OrderView';
import PaymentSuccessView from './views/PaymentSuccessView';
import LoginView from './views/LoginView';
import NotFoundView from './views/NotFoundView';
import LandingView from './views/LandingView';
import DemoView from './views/DemoView';
import TableWaitingView from './views/TableWaitingView';
import { DemoProvider } from './context/DemoContext';

import PathService from './services/path.service';
import OrderTableService from './services/order-table.service';

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathSegments = React.useMemo(() => {
    return PathService.parsePathSegments(location.pathname);
  }, [location.pathname]);

  const [currentURL, setCurrentURL] = useState<string>('');
  const [orderId, setOrderId] = useState<string | number>('');
  const [isDemo, setIsDemo] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(
    pathSegments.firstSegment === 'o' || pathSegments.firstSegment === 't'
  );
  const requestRef = useRef(0);

  const processOrderTable = useCallback(async (path1: string, path2: string): Promise<void> => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setOrderId('');
    setCurrentURL('');
    setIsDemo(false);
    try {
      const result = await OrderTableService.processOrderTablePath(path1, path2);
      if (requestId !== requestRef.current) {
        return;
      }

      setOrderId(result.orderId ?? '');
      setCurrentURL(result.currentURL);
      setIsDemo(Boolean(result.isDemo));

      if (path1 === 't' && result.currentURL.startsWith('/o/')) {
        navigate(result.currentURL, { replace: true });
      }
    } catch (error) {
      console.error('Error processing path:', error);
      if (requestId !== requestRef.current) {
        return;
      }
      setOrderId('');
      setCurrentURL('error');
      setIsDemo(false);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (PathService.pathNeedsFixing(location.pathname)) {
      const fixedPath = PathService.fixPath(location.pathname);
      navigate(fixedPath, { replace: true });
      return;
    }

    const path1 = pathSegments.firstSegment;
    if (path1 === 'o' || path1 === 't') {
      processOrderTable(path1, pathSegments.secondSegment);
    } else if (path1 !== 'demo') {
      setIsDemo(false);
    }
  }, [pathSegments, navigate, location.pathname, processOrderTable]);

  const path1 = pathSegments.firstSegment;
  const path2 = pathSegments.secondSegment;
  const demoMode = path1 === 'demo' || isDemo;

  let page: React.ReactNode = <NotFoundView />;

  if (!path1) {
    page = <LandingView />;
  } else if (path1 === 'login') {
    page = <LoginView />;
  } else if (path1 === 'paymentsuccess') {
    page = <PaymentSuccessView orderId={path2} />;
  } else if (path1 === 'demo') {
    page = <DemoView />;
  } else if (path1 === 'o' || path1 === 't') {
    if (orderId && currentURL === `/o/${orderId}`) {
      page = (
        <OrderView
          orderId={orderId}
          currentURL={currentURL}
          userData={null}
        />
      );
    } else if (path1 === 't' && !loading && currentURL === `/t/${path2}`) {
      page = <TableWaitingView tableNumber={path2} />;
    } else if (loading) {
      page = <div className="container mt-5 text-center">Loading...</div>;
    }
  }

  return <DemoProvider isDemo={demoMode}>{page}</DemoProvider>;
};

export default App;
